import { useEffect, useRef } from 'react';

import { isEditableShortcutTarget } from '@apps/web/lib/keyboard_shortcuts';

// Registers a window-level keydown shortcut for a single key. The handler is
// held in a ref so the listener stays stable across renders (no re-register on
// every render) while always seeing the latest closure.
//
// The shortcut is skipped when: a modifier is held (so it never clobbers
// browser/OS shortcuts), the event was already handled by another listener
// (`defaultPrevented`, so stacked shortcuts compose instead of conflicting), or
// focus is in a text field (so it doesn't hijack typing). The handler decides
// whether to `preventDefault()`.
export function useGlobalHotkey(
    key: string,
    handler: (event: KeyboardEvent) => void,
    options?: { readonly enabled?: boolean },
): void {
    const enabled = options?.enabled ?? true;
    const handlerRef = useRef(handler);
    handlerRef.current = handler;

    useEffect(() => {
        if (!enabled) {
            return;
        }
        function onKeyDown(event: KeyboardEvent) {
            if (
                event.defaultPrevented ||
                event.metaKey ||
                event.ctrlKey ||
                event.altKey ||
                event.key !== key ||
                isEditableShortcutTarget(event.target)
            ) {
                return;
            }
            handlerRef.current(event);
        }
        window.addEventListener('keydown', onKeyDown);
        return () => window.removeEventListener('keydown', onKeyDown);
    }, [key, enabled]);
}
